import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, Calculator, Tv } from "lucide-react";
import { faDateTime, faNum } from "@/lib/format";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

const Coin3D = lazy(() => import("@/components/Coin3D").then((m) => ({ default: m.Coin3D })));
import { useInView } from "framer-motion";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 1600);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, to]);
  return (
    <span ref={ref}>
      {faNum(v)}
      {suffix}
    </span>
  );
};

const scrollTo = (e, href) => {
  e.preventDefault();
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = ({ board }) => {
  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden pt-24">
      <div className="hero-grid-bg absolute inset-0" />
      <div className="absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-amber-500/10 blur-[140px]" />
      <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-yellow-600/10 blur-[120px]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
        <div>
          <h1 data-testid="hero-title" className="text-4xl font-black leading-[1.15] tracking-tight text-amber-50 sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.3}>تابلوی نرخ روز</MaskedLine>
            <MaskedLine delay={0.45} className="text-gold-grad">سکه کازرانی</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-6 max-w-xl text-base font-light leading-8 text-stone-400"
          >
            استعلام آنلاین نرخ خرید و فروش انواع سکه، سکه‌های پارسیان و شاخص‌های بازار طلا — با تضمین اصالت، پلمپ رسمی و نرخ‌گذاری شفاف اتحادیه.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#rates"
              data-testid="hero-cta-rates"
              onClick={(e) => scrollTo(e, "#rates")}
              className="gold-sheen group flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3 text-sm font-bold text-black transition-colors duration-300 hover:bg-amber-400"
            >
              مشاهده تابلوی نرخ
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#parsian"
              data-testid="hero-cta-parsian"
              onClick={(e) => scrollTo(e, "#parsian")}
              className="flex items-center gap-2 rounded-full border border-amber-500/30 px-7 py-3 text-sm font-medium text-amber-200 transition-colors duration-300 hover:border-amber-400 hover:bg-amber-500/10"
            >
              <Calculator className="h-4 w-4" />
              سکه‌های پارسیان
            </a>
            <Link
              to="/tv"
              data-testid="hero-cta-tv"
              className="flex items-center gap-2 rounded-full border border-amber-500/30 px-7 py-3 text-sm font-medium text-amber-200 transition-colors duration-300 hover:border-amber-400 hover:bg-amber-500/10"
            >
              <Tv className="h-4 w-4" />
              حالت تابلوی مغازه
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-amber-500/15 pt-6"
          >
            <div data-testid="hero-stat-experience">
              <div className="font-display text-2xl font-bold text-amber-300"><Counter to={10} suffix="+" /></div>
              <div className="mt-1 text-xs text-stone-500">سال تجربه</div>
            </div>
            <div data-testid="hero-stat-coins">
              <div className="font-display text-2xl font-bold text-amber-300"><Counter to={board?.coins?.length || 8} /></div>
              <div className="mt-1 text-xs text-stone-500">قطعه سکه در تابلو</div>
            </div>
            <div data-testid="hero-stat-parsian">
              <div className="font-display text-2xl font-bold text-amber-300"><Counter to={board?.parsian?.length || 18} /></div>
              <div className="mt-1 text-xs text-stone-500">وزن پارسیان</div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <Suspense fallback={<div className="h-[380px] w-full sm:h-[480px] lg:h-[560px]" />}>
            <Coin3D />
          </Suspense>
          <p className="pointer-events-none mt-2 text-center text-[11px] text-stone-600">سکه را بگیرید و بچرخانید</p>
          <div data-testid="hero-updated-badge" className="pointer-events-none absolute bottom-14 right-1/2 translate-x-1/2 rounded-full border border-amber-500/20 bg-black/60 px-4 py-1.5 text-[11px] text-amber-200/70 backdrop-blur-md sm:bottom-16">
            آخرین به‌روزرسانی: {faDateTime(board?.updated_at)}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
